import type { ClefName } from 'shared/definitions/musicTheory/clefs';
import type { ModeName } from 'shared/definitions/musicTheory/scales';
import type { SolfegeSystemName } from 'shared/definitions/musicTheory/solfegeAndScaleDegrees';
import {
  scaleDegreeNamesMajor,
  scaleDegreeNamesMinor,
} from 'shared/definitions/musicTheory/solfegeAndScaleDegrees';
import { romanQualities } from 'shared/definitions/musicTheory/romanNumerals';
import KeySignature from '../uTheory.Notation/keySignature';
import RomanNumeral from '../uTheory.Notation/romanNumeral';
import type Note from '../uTheory.Notation/note';
import { getSolfegeSystemsMapForScaleMode } from './getSolfegeSystemsMapForScaleMode';
import type { ScaleDegree, SolfegePresentationSystem } from './solfegeDefinitions';
import { isScaleDegree } from './solfegeDefinitions';
import rejectDuplicates from '../uTheory.Skills/rejectDuplicates';

export interface ScaleDegreeNoteIDQuestion {
  text: string[];
  key: KeySignature;
  skills: (string | number)[];
  degree: ScaleDegree;
  degreeName: string;
  note: Note;
  clef: ClefName;
  mode: ModeName | 'allMinors';
}

export function getRomanFromScaleDegree(degree: string | number, key: KeySignature) {
  degree = `${degree}`;
  if (key.quality === 'minor') {
    if (degree === '7') degree = 'b7';
    if (degree === 'M7') degree = '7';
  }
  const roman = romanQualities[key.quality][degree][0];
  return new RomanNumeral(key, roman);
}

/**
 * Convert a degree to an HTML formatted string.
 * @param degree - '1' etc...
 * @param key - KeySignature
 * @param presentationSystem - names => "tonic", numbers => ^1, romans => "I", solfege => "Do"
 */
function scaleDegreeToDegreeDisplayString(
  degree: ScaleDegree,
  key: KeySignature,
  presentationSystem: SolfegePresentationSystem | 'romans',
  solfegeSystem: SolfegeSystemName
): string {
  degree = `${degree}`;

  if (presentationSystem === 'names') {
    if (key.quality === 'minor') return scaleDegreeNamesMinor[degree];
    return scaleDegreeNamesMajor[degree];
  }

  if (presentationSystem === 'romans') {
    const rn = getRomanFromScaleDegree(degree, key);
    return rn.toAccidentalFontString();
  }

  // From here on out, we'll need a solfege map.
  const mode = key.quality === 'minor' ? 'allMinors' : 'Major';
  const solfegeMap = getSolfegeSystemsMapForScaleMode(mode, solfegeSystem);
  const solfegeIndex = solfegeMap.scaleDegrees.findIndex((sdData) => sdData.name === degree);
  if (solfegeIndex === -1) {
    throw Error(`${degree} not found in solfege/scale-degree map!`);
  }

  if (presentationSystem === 'scaleDegrees') {
    return `<span class="accidentals">${solfegeMap.scaleDegrees[solfegeIndex].displayString}</span>`;
  }

  if (presentationSystem === 'solfege') {
    return solfegeMap.solfege[solfegeIndex];
  }

  if (presentationSystem === 'handSigns') {
    throw Error('Hand signs are not yet supported in note <--> scale degree questions.');
  }

  throw Error(
    `Degree system must be one of names, scaleDegrees or romans, not ${presentationSystem}`
  );
}

/**
 * Returns a random octave of so that that note fits in the specified clef with maxmimum 2 ledger lines
 */
function staffRandomOctave(note: Note, clef?: ClefName): number {
  note.octave = Math.floor(Math.random() * 5) + 2;
  const newNote = note.clone();
  let emergencyExit = 0;
  const randomClefs: ClefName[] = ['treble', 'bass'];
  if (!clef) clef = randomClefs[~~(Math.random() * 2)];
  while (!newNote.prefersClef().includes(clef) && emergencyExit++ < 1000) {
    newNote.octave = Math.floor(Math.random() * 5) + 2;
  }

  return newNote.octave;
}

export type LabelingSolfegeSkill =
  | 'labeling_solfege_major'
  | 'labeling_solfege_natural_minor'
  | 'labeling_solfege_harmonic_minor'
  | 'labeling_solfege_all_minors';

const labelingSolfegeSkillNames: LabelingSolfegeSkill[] = [
  'labeling_solfege_major',
  'labeling_solfege_natural_minor',
  'labeling_solfege_harmonic_minor',
  'labeling_solfege_all_minors',
];

export function isLabelingSolfegeSkill(skill: string): skill is LabelingSolfegeSkill {
  return labelingSolfegeSkillNames.includes(skill as LabelingSolfegeSkill);
}

export type WritingSolfegeSkill =
  | 'writing_solfege_major'
  | 'writing_solfege_natural_minor'
  | 'writing_solfege_harmonic_minor'
  | 'writing_solfege_all_minors';

const writingSolfegeSkillNames: WritingSolfegeSkill[] = [
  'writing_solfege_major',
  'writing_solfege_natural_minor',
  'writing_solfege_harmonic_minor',
  'writing_solfege_all_minors',
];

export function isWritingSolfegeSkill(skill: string): skill is WritingSolfegeSkill {
  return writingSolfegeSkillNames.includes(skill as WritingSolfegeSkill);
}

export type ConvertingSolfegeSkill =
  | 'scale_degree_conversion_major'
  | 'scale_degree_conversion_minor';

const convertingSolfegeSkillNames: ConvertingSolfegeSkill[] = [
  'scale_degree_conversion_major',
  'scale_degree_conversion_minor',
];

export function isConvertingSolfegeSkill(skill: string): skill is ConvertingSolfegeSkill {
  return convertingSolfegeSkillNames.includes(skill as ConvertingSolfegeSkill);
}

export function scaleDegreeNoteSkillsToAvailableModes(
  skills: string[]
): (ModeName | 'allMinors')[] {
  skills = skills.filter(
    (s) => isWritingSolfegeSkill(s) || isLabelingSolfegeSkill(s) || isConvertingSolfegeSkill(s)
  );

  const availableModes: (ModeName | 'allMinors')[] = [];
  // Major
  if (
    skills.includes('labeling_solfege_major') ||
    skills.includes('writing_solfege_major') ||
    skills.includes('scale_degree_conversion_major')
  ) {
    availableModes.push('Major');
  }

  // natural minor
  if (
    skills.includes('labeling_solfege_natural_minor') ||
    skills.includes('writing_solfege_natural_minor') ||
    skills.includes('scale_degree_conversion_minor')
  ) {
    availableModes.push('naturalMinor');
  }

  // harmonic minor
  if (
    skills.includes('labeling_solfege_harmonic_minor') ||
    skills.includes('writing_solfege_harmonic_minor') ||
    skills.includes('scale_degree_conversion_minor')
  ) {
    availableModes.push('harmonicMinor');
  }

  // all minors
  if (
    skills.includes('writing_solfege_all_minors') ||
    skills.includes('scale_degree_conversion_minor')
  ) {
    availableModes.push('allMinors');
  }

  // scale degree names might not give us an available mode when
  // trained via skills:
  if (!availableModes.length) {
    availableModes.push('allMinors', 'Major');
  }
  return availableModes;
}

export function modeAndPresentationSystemToSkills(
  mode: ModeName | 'allMinors',
  presentationSystem: SolfegePresentationSystem | 'romans',
  labelOrWrite: 'label' | 'write'
): string[] {
  const skills: (
    | LabelingSolfegeSkill
    | WritingSolfegeSkill
    | ConvertingSolfegeSkill
    | 'scale_degree_names'
  )[] = [];

  if (presentationSystem === 'names') {
    skills.push('scale_degree_names');
  }

  if (mode === 'Major') {
    skills.push('scale_degree_conversion_major');
    skills.push(labelOrWrite === 'label' ? 'labeling_solfege_major' : 'writing_solfege_major');
    return skills;
  }

  skills.push('scale_degree_conversion_minor');
  if (mode === 'naturalMinor') {
    skills.push(
      labelOrWrite === 'label' ? 'labeling_solfege_natural_minor' : 'writing_solfege_natural_minor'
    );
  }

  if (mode === 'harmonicMinor') {
    skills.push(
      labelOrWrite === 'label'
        ? 'labeling_solfege_harmonic_minor'
        : 'writing_solfege_harmonic_minor'
    );
  }

  if (
    mode === 'allMinors' ||
    mode === 'melodicMinorAscending' ||
    mode === 'melodicMinorDescending'
  ) {
    skills.push(
      labelOrWrite === 'label' ? 'labeling_solfege_all_minors' : 'writing_solfege_all_minors'
    );
  }

  return skills;
}

function getIdentifyTonicSkills(key: KeySignature): string {
  if (key.equals('A minor')) return 'identify_tonic_a_minor';
  if (key.equals('C Major')) return 'identify_tonic_c_major';
  if (key.quality === 'Major' && key.accidentalsCount > 0) return 'identify_tonic_major_sharp_keys';
  if (key.quality === 'Major' && key.accidentalsCount < 0) return 'identify_tonic_major_flat_keys';
  if (key.quality === 'minor' && key.accidentalsCount > 0) return 'identify_tonic_minor_sharp_keys';
  if (key.quality === 'minor' && key.accidentalsCount < 0) return 'identify_tonic_minor_flat_keys';
  throw Error(`Invalid key in scaleDegreeNoteQuestionGenerator: ${key}}`);
}

function keyToScaleDegreeIdentifyTonicQuestion(
  key: KeySignature,
  presentationSystem: SolfegePresentationSystem,
  solfegeSystem: SolfegeSystemName,
  questionNumber: number
): ScaleDegreeNoteIDQuestion {
  const skills = [getIdentifyTonicSkills(key)];
  const clefs: ClefName[] = ['treble', 'bass'];
  const clef = clefs[Math.floor(Math.random() * clefs.length)];
  const mode = key.quality === 'Major' ? 'Major' : 'naturalMinor';
  const degreeLabel =
    presentationSystem === 'scaleDegrees' || solfegeSystem === 'scaleDegrees'
      ? 'tonic'
      : scaleDegreeToDegreeDisplayString('1', key, presentationSystem, solfegeSystem);
  return {
    key,
    mode,
    degree: '1',
    degreeName: scaleDegreeToDegreeDisplayString('1', key, 'names', 'scaleDegrees'),
    skills,
    text: [degreeLabel, key.toAccidentalFontString(), `${questionNumber + 1}`],
    note: key.tonic.clone(),
    clef,
  };
}

export function processManualScaleDegreeIdentifyTonicQuestions(
  questions: string[],
  presentationSystem: SolfegePresentationSystem,
  solfegeSystem: SolfegeSystemName
): ScaleDegreeNoteIDQuestion[] {
  return questions
    .map((q) => new KeySignature(q))
    .map((key, i) =>
      keyToScaleDegreeIdentifyTonicQuestion(key, presentationSystem, solfegeSystem, i)
    );
}

type IdentifyTonicSkill =
  | 'identify_tonic_c_major'
  | 'identify_tonic_major_sharp_keys'
  | 'identify_tonic_major_flat_keys'
  | 'identify_tonic_a_minor'
  | 'identify_tonic_minor_sharp_keys'
  | 'identify_tonic_minor_flat_keys';

export function isIdentifyTonicSkill(skill: string): skill is IdentifyTonicSkill {
  return [
    'identify_tonic_c_major',
    'identify_tonic_major_sharp_keys',
    'identify_tonic_major_flat_keys',
    'identify_tonic_a_minor',
    'identify_tonic_minor_sharp_keys',
    'identify_tonic_minor_flat_keys',
  ].includes(skill);
}

function keysFromIdentifyTonicSkills(
  skills: IdentifyTonicSkill[],
  numberOfQuestions: number
): KeySignature[] {
  const keys: KeySignature[] = [];

  if (skills.includes('identify_tonic_c_major')) {
    keys.push(new KeySignature('C Major'), new KeySignature('C Major'));
    skills = skills.filter((s) => s !== 'identify_tonic_c_major');
  }
  if (skills.includes('identify_tonic_a_minor')) {
    keys.push(new KeySignature('A minor'), new KeySignature('A minor'));
    skills = skills.filter((s) => s !== 'identify_tonic_a_minor');
  }

  const keysPerRemainingGroup = Math.min(
    Math.ceil((numberOfQuestions - keys.length) / skills.length),
    7
  );
  const majorKeys = KeySignature.allKeys('Major');
  if (skills.includes('identify_tonic_major_flat_keys')) {
    keys.push(
      ...rejectDuplicates(
        majorKeys.filter((k) => k.accidentalsCount < 0),
        keysPerRemainingGroup
      )
    );
  }
  if (skills.includes('identify_tonic_major_sharp_keys')) {
    keys.push(
      ...rejectDuplicates(
        majorKeys.filter((k) => k.accidentalsCount > 0),
        keysPerRemainingGroup
      )
    );
  }

  const minorKeys = KeySignature.allKeys('minor');
  if (skills.includes('identify_tonic_minor_flat_keys')) {
    keys.push(
      ...rejectDuplicates(
        minorKeys.filter((k) => k.accidentalsCount < 0),
        keysPerRemainingGroup
      )
    );
  }
  if (skills.includes('identify_tonic_minor_sharp_keys')) {
    keys.push(
      ...rejectDuplicates(
        minorKeys.filter((k) => k.accidentalsCount > 0),
        keysPerRemainingGroup
      )
    );
  }

  if (keys.length > numberOfQuestions) keys.length = numberOfQuestions;
  return keys;
}

export function generateScaleDegreeIdentifyTonicQuestions(
  numQuestions = 10,
  skills: IdentifyTonicSkill[],
  presentationSystem: SolfegePresentationSystem,
  solfegeSystem: SolfegeSystemName = 'solfegeDoMinor'
): ScaleDegreeNoteIDQuestion[] {
  const possibleKeys = keysFromIdentifyTonicSkills(skills, numQuestions);
  if (possibleKeys.length < numQuestions) numQuestions = possibleKeys.length;
  const keys = rejectDuplicates(possibleKeys, numQuestions);

  const clefs: ClefName[] = rejectDuplicates(['treble', 'bass'], keys.length);
  return keys.map(
    (key, i): ScaleDegreeNoteIDQuestion => ({
      ...keyToScaleDegreeIdentifyTonicQuestion(key, presentationSystem, solfegeSystem, i),
      clef: clefs[i],
    })
  );
}

export function generateScaleDegreeNoteQuestions(
  numQuestions = 10,
  availableModes: (ModeName | 'allMinors')[],
  presentationSystem: SolfegePresentationSystem | 'romans',
  solfegeSystem: SolfegeSystemName = 'solfegeDoMinor',
  changeKeyEvery: number = 4,
  labelOrWrite: 'label' | 'write'
): ScaleDegreeNoteIDQuestion[] {
  const keysNeeded = Math.ceil(numQuestions / changeKeyEvery);
  const modesToUse =
    availableModes.length > 1
      ? rejectDuplicates(availableModes, keysNeeded)
      : new Array(keysNeeded).fill(availableModes[0]);
  const keyGroups = KeySignature.randomKeys(keysNeeded, 'Major').map((key, i) => ({
    key: modesToUse[i] === 'Major' ? key : key.getRelativeKey(),
    mode: modesToUse[i],
  }));

  return keyGroups
    .map(({ mode, key }) => {
      const availableDegrees = getSolfegeSystemsMapForScaleMode(
        mode,
        'scaleDegrees'
      ).scaleDegrees.map((sd) => sd.name);

      let clef: ClefName;
      return rejectDuplicates(availableDegrees, changeKeyEvery).map((degree) => {
        const note = key.getNoteAtScaleDegree(degree);
        if (!note) throw Error(`No note exists at scale degree ${degree} in key ${key}`);
        note.octave = staffRandomOctave(note, clef);
        if (!clef) {
          const clefs = note.prefersClef();
          clef = clefs[~~(Math.random() * clefs.length)];
        }
        return {
          key,
          mode,
          degree,
          degreeName: scaleDegreeToDegreeDisplayString(degree, key, 'names', 'scaleDegrees'),
          skills: modeAndPresentationSystemToSkills(mode, presentationSystem, labelOrWrite),
          text: [
            scaleDegreeToDegreeDisplayString(degree, key, presentationSystem, solfegeSystem),
            key.toAccidentalFontString(),
          ],
          note,
          clef,
        };
      });
    })
    .reduce((accum, questions) => accum.concat(questions), [])
    .map((question, i) => {
      question.text.push(`${i + 1}`);
      return question;
    })
    .slice(0, numQuestions);
}

/**
 * Processes manual Scale Note Degree questions:
 *
 * Single question format:
 * “Bb Maj:3”, “Bb: 3", “Bb m : #7”, 3, '3' are all legitimate questions
 *
 * @param questions an array of individual questions. For example:
 * ['Bb min: 4', 3, '#7', '#6', 6, '5', 'Bb m: 2', 'C  : 5']
 */
export function processManualScaleDegreeNoteQuestions(
  questions: (string | number)[],
  presentationSystem: SolfegePresentationSystem | 'romans',
  solfegeSystem: SolfegeSystemName = 'solfegeDoMinor',
  labelOrWrite: 'label' | 'write'
): ScaleDegreeNoteIDQuestion[] {
  if (!Array.isArray(questions)) {
    throw new Error('scaleDegreeToNote and noteToScaleDegree questions must be in array format.');
  }

  let key = new KeySignature('C');
  let clef: ClefName;
  let mode: ModeName | 'allMinors' = 'Major';
  const returnQuestions = questions.map((question, i) => {
    // Cast all questions into string
    question = `${question}`;
    if (question.includes('/')) {
      const questionSplit = question.split('/');
      const newModeName = questionSplit[1].trim();
      if (!['Major', 'naturalMinor', 'harmonicMinor', 'allMinors'].includes(newModeName)) {
        throw Error(`${newModeName} is not a valid mode for scale degree <--> note exercises.`);
      }
      mode = newModeName as ModeName | 'allMinors';
      question = questionSplit[0];
    }

    if (question.includes(':')) {
      const questionSplit = question.split(':');
      key = new KeySignature(questionSplit[0].trim());
      clef = undefined;
      question = questionSplit[1];
      if (key.quality === 'Major' && mode !== 'Major') mode = 'Major';
      if (key.quality === 'minor' && mode === 'Major') mode = 'allMinors';
    }
    const degree = question.trim().replace('#', 'M');
    if (!isScaleDegree(degree)) {
      throw Error(`${degree} is not a valid scale degree name.`);
    }
    const note = key.getNoteAtScaleDegree(degree);
    if (!note) throw Error(`No note exists at scale degree ${degree} in key ${key}`);
    note.octave = staffRandomOctave(note, clef);
    if (!clef) {
      const clefs = note.prefersClef();
      clef = clefs[~~(Math.random() * clefs.length)];
    }
    return {
      key,
      mode,
      degree,
      degreeName: scaleDegreeToDegreeDisplayString(degree, key, 'names', 'solfegeDoMinor'),
      text: [
        scaleDegreeToDegreeDisplayString(degree, key, presentationSystem, solfegeSystem),
        key.toAccidentalFontString(),
        `${i + 1}`,
      ],
      skills: modeAndPresentationSystemToSkills(mode, presentationSystem, labelOrWrite),
      note,
      clef,
    };
  });

  return returnQuestions;
}
